import { useCallback, useMemo } from 'react';
import { Permissions } from '../../../../core/permissions/permissions.typings';
import { useQueryGetProjectOptional, useQueryListProjects } from '../../../../core/queries/projects.queries';
import { useMutationUpdateProject } from '../../settings.queries';
import { EntityForm } from '../form.types';
import { ProjectSettingsForm } from './form';
import { ProjectSettingsFormOutput } from './form-output';
import { ProjectSettingsFormSchema, ProjectSettingsFormVariant, projectSettingsFormVariant } from './form-schema';
import {
  carrierProjectSettingsFormUISchema,
  projectSettingsFormUISchema,
  regionProjectSettingsFormUISchema
} from './ui-schema';
import { useHasUIFeatures } from '../../../../core/permissions/has-permissions.hook';
import { UiSchema } from '@rjsf/utils';

export function useEditProjectForm(
  projectID: string | undefined,
  formData: ProjectSettingsForm | undefined,
  readOnly: boolean = false
): EntityForm | null {
  const projectResult = useQueryGetProjectOptional(projectID);
  const projectMutation = useMutationUpdateProject();
  const { hasAllFeatures: isExperimental } = useHasUIFeatures(['experimental']);
  const parentProjectResult = useQueryListProjects({ permission: Permissions.projects.create });
  const parentProjectOptions = useMemo(
    () =>
      (parentProjectResult.data || [])
        .filter((p) => p.id !== projectID)
        .sort((a, b) => {
          return a.name.localeCompare(b.name);
        }),
    [parentProjectResult.data, projectID]
  );

  const defaultValues = useMemo(() => {
    return ProjectSettingsForm.GetInitialData(projectResult.data);
  }, [projectResult.data]);

  const initialFormOutput = useMemo(() => {
    return new ProjectSettingsFormOutput(defaultValues);
  }, [defaultValues]);

  const currentFormOutput = useMemo(() => {
    return new ProjectSettingsFormOutput({
      ...initialFormOutput,
      ...formData
    });
  }, [formData, initialFormOutput]);

  // From the loaded project, not the form: type is read-only for carriers.
  const variant = projectSettingsFormVariant(projectResult.data?.projectType);

  const formSchema = useMemo(() => {
    return ProjectSettingsFormSchema.FromProjectData(
      currentFormOutput,
      parentProjectOptions,
      readOnly,
      isExperimental,
      variant
    );
  }, [currentFormOutput, parentProjectOptions, readOnly, isExperimental, variant]);

  const onChangeInterceptor = useCallback((currentFormData: ProjectSettingsForm, newFormData: ProjectSettingsForm) => {
    const newFormDataCopy = { ...newFormData };
    // If the Country has changed, we need to clear the selected TimeZone
    if (currentFormData.timezone && currentFormData.country !== newFormData.country) {
      newFormDataCopy.timezone = undefined;
    }
    return newFormDataCopy;
  }, []);

  const onSubmit = useCallback(
    async (formOutput: ProjectSettingsFormOutput) => {
      if (!projectID) {
        return;
      }
      const result = await projectMutation.mutateAsync({
        projectID,
        dto: ProjectSettingsFormOutput.ToEditProjectDto(formOutput)
      });
      await projectResult.refetch();
      return {
        entityId: result.id,
        parentEntityId: result.parentID
      };
    },
    [projectID, projectMutation, projectResult]
  );

  if (!projectID) {
    return null;
  }

  if (!formSchema) {
    return null;
  }

  return {
    isLoading: projectResult.isLoading || parentProjectResult.isLoading,
    title: TITLES[variant],
    subtitle: projectID,
    allowSubtitleCopy: true,
    defaultValues,
    initialFormOutput: initialFormOutput,
    formSchema,
    uiSchema: UI_SCHEMAS[variant],
    getOutput(formData) {
      return new ProjectSettingsFormOutput(formData as ProjectSettingsForm);
    },
    onChangeInterceptor,
    onSubmit,
    isLocked: false,
    lockedMessage: null
  };
}

const TITLES: Record<ProjectSettingsFormVariant, string> = {
  standard: 'Project Settings',
  carrier: 'Carrier Project Settings',
  region: 'Region Project Settings'
};

const UI_SCHEMAS: Record<ProjectSettingsFormVariant, UiSchema> = {
  standard: projectSettingsFormUISchema,
  carrier: carrierProjectSettingsFormUISchema,
  region: regionProjectSettingsFormUISchema
};
